import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../support/store';
import JourneyMapAdminListPage from './JourneyMapAdminListPage';
import JourneyMapBuilderPage from './JourneyMapBuilderPage';
import { useJourneyMaps } from '../../../hooks/journey-map-hooks';
import {
  resetBuilderState,
  setActiveDraft,
} from '../../../support/slices/journeyMapBuilderSlice';
import { loadJourneyMapDraft } from '../../../support/journeyMapPersistence';

type AdminViewMode = 'list' | 'builder';

export default function JourneyMapAdminPage() {
  const dispatch = useAppDispatch();
  const journeyMaps = useJourneyMaps();
  const [mode, setMode] = useState<AdminViewMode>('list');
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const activeDraftId = useSelector(
    (state: RootState) => state.journeyMapBuilder.activeDraftId,
  );

  const activeDraft = useSelector((state: RootState) =>
    state.journeyMapBuilder.drafts.find((d) => d.tempId === activeDraftId) || null,
  );

  const activeDraftTitle = useMemo(() => {
    if (!activeDraft) return 'Journey Map Builder';
    return activeDraft.name?.trim() || 'Untitled Journey Map';
  }, [activeDraft]);

  const handleCreateNew = useCallback(() => {
    setLoadError(null);
    dispatch(resetBuilderState());
    setMode('builder');
  }, [dispatch]);

  const handleEditJourneyMap = useCallback(
    async (journeyMapId: number) => {
      try {
        setLoadError(null);
        setIsLoadingMap(true);

        const existing = journeyMaps.find((map) => map.id === journeyMapId);
        if (!existing) {
          setLoadError(`Journey Map ${journeyMapId} was not found.`);
          return;
        }

        const draft = await loadJourneyMapDraft(existing);
        dispatch(setActiveDraft(draft));
        setMode('builder');
      } catch (error: any) {
        console.error('Failed to load Journey Map details', error);
        setLoadError(error?.message || 'Failed to load Journey Map details.');
      } finally {
        setIsLoadingMap(false);
      }
    },
    [dispatch, journeyMaps],
  );

  const handleBackToList = useCallback(() => {
    setMode('list');
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 220px)',
        minHeight: 650,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {mode === 'list' ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {loadError && (
            <Box
              sx={{
                px: 2,
                py: 1,
                color: 'error.main',
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'error.lighter',
              }}
            >
              <Typography variant="body2">{loadError}</Typography>
            </Box>
          )}

          <JourneyMapAdminListPage
            onCreateNew={handleCreateNew}
            onEditJourneyMap={handleEditJourneyMap}
          />

          {isLoadingMap && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.45)',
                zIndex: 10,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography variant="body2">Loading Journey Map…</Typography>
              </Stack>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToList}
                >
                  Back to List
                </Button>

                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {activeDraftTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Edit journey structure, branching flow, and item details.
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <JourneyMapBuilderPage />
          </Box>
        </Box>
      )}
    </Box>
  );
}